import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useBooking } from "../context/BookingContext";
import emblem from "../assets/emblem.png";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/ndis", label: "NDIS" },
  { to: "/reviews", label: "Reviews" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
    <img src={emblem} alt="Sora Speech Co. logo" className="h-11 w-11 object-contain transition-transform duration-300 group-hover:scale-105" />
    <span className="flex flex-col leading-none">
      <span className="font-display text-xl text-sora-slate tracking-tight">Sora Speech Co.</span>
      <span className="text-[10px] uppercase tracking-[0.28em] text-sora-blue mt-1">Paediatric Speech Pathology</span>
    </span>
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openBooking } = useBooking();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-sora-bg/85 border-b border-sora-stone/25 py-3" : "bg-transparent py-5"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
        <Logo />
        <div className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`text-sm tracking-wide transition-colors relative after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-sora-blue after:transition-all after:duration-300 ${
                pathname === l.to ? "text-sora-slate after:w-full" : "text-sora-charcoal/75 hover:text-sora-slate after:w-0 hover:after:w-full"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="nav-book-now"
            onClick={() => openBooking("booking")}
            className="hidden sm:inline-flex bg-sora-blue text-white rounded-[20px] px-6 py-2.5 text-sm font-bold tracking-wide hover:bg-sora-slate transition-colors duration-300 shadow-sm"
          >
            Book Now
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden grid place-items-center h-10 w-10 rounded-full bg-sora-warm border border-sora-stone/40 text-sora-slate"
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden mt-3 mx-4 rounded-[24px] bg-sora-warm border border-sora-stone/30 shadow-[0_20px_50px_rgba(63,88,115,0.12)] p-5">
          <div className="flex flex-col">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`mobile-link-${l.label.toLowerCase()}`}
                className={`py-3 px-2 rounded-xl text-base ${pathname === l.to ? "text-sora-slate font-bold bg-sora-sage/25" : "text-sora-charcoal/80"}`}
              >
                {l.label}
              </Link>
            ))}
            <button
              data-testid="mobile-book-now"
              onClick={() => openBooking("booking")}
              className="mt-3 bg-sora-blue text-white rounded-[18px] px-6 py-3 font-bold hover:bg-sora-slate transition-colors"
            >
              Book Now
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
